<script setup>
import { ref, computed } from 'vue';
import SzContactsImportAPI from 'dashboard/api/szContactsImport';

const props = defineProps({
  file: { type: Object, default: null },
  errors: { type: Object, default: () => ({ invalid: [], missing: [], duplicated: [] }) },
  total: { type: Number, default: 0 },
  hasBlockingErrors: { type: Boolean, default: false },
});
const emit = defineEmits(['fileSelected', 'clear']);

const downloadingTemplate = ref(false);
// Served by nginx (solarz-hub static), not bundled by Vite — use a runtime
// binding so Rollup doesn't try to resolve it during the production build.
const folderIconUrl = '/static/img/sz-import-pasta.png';

async function downloadTemplate() {
  if (downloadingTemplate.value) return;
  downloadingTemplate.value = true;
  try {
    await SzContactsImportAPI.downloadTemplate();
  } catch (_) {
    alert('Não foi possível baixar a planilha modelo.');
  } finally {
    downloadingTemplate.value = false;
  }
}

const inputRef = ref(null);
const dragOver = ref(false);
const uploadProgress = ref(0);
const uploading = ref(false);

const hasErrors = computed(() =>
  Object.values(props.errors).some(arr => arr.length)
);
const hasOnlyDuplicates = computed(() =>
  props.errors.duplicated.length > 0 && !props.hasBlockingErrors
);

function clickPick() {
  inputRef.value?.click();
}
function onPick(e) {
  const f = e.target.files?.[0];
  if (f) handleFile(f);
}
function onDrop(e) {
  e.preventDefault();
  dragOver.value = false;
  const f = e.dataTransfer.files?.[0];
  if (f) handleFile(f);
}
async function handleFile(f) {
  if (!/\.xlsx$/i.test(f.name)) {
    alert('Apenas arquivos .xlsx são aceitos.');
    return;
  }
  uploading.value = true;
  uploadProgress.value = 10;
  // We can't observe real fetch progress easily here; simulate UX with a quick
  // ramp while the parent does the actual upload. Once parent updates `total`
  // or `errors` we hit 100%.
  const ramp = setInterval(() => {
    uploadProgress.value = Math.min(90, uploadProgress.value + 10);
  }, 120);
  try {
    await emit('fileSelected', f);
  } finally {
    clearInterval(ramp);
    uploadProgress.value = 100;
    uploading.value = false;
  }
}
function clearFile() {
  uploadProgress.value = 0;
  uploading.value = false;
  emit('clear');
}
function fmtLines(arr) {
  return arr.map(n => String(n).padStart(2, '0')).join(', ');
}
</script>

<template>
  <div class="sz-step-upload">
    <header class="sz-step-header">
      <h4>Upload de planilha</h4>
      <span class="sz-step-hint">*Apenas a planilha modelo é aceita</span>
      <button
        type="button"
        class="sz-template-btn"
        :disabled="downloadingTemplate"
        @click="downloadTemplate"
      >
        {{ downloadingTemplate ? 'Baixando…' : 'Baixar planilha modelo' }}
      </button>
    </header>

    <div
      class="sz-dropzone"
      :class="{
        'sz-drop-over': dragOver,
        'sz-drop-error': hasBlockingErrors && file,
        'sz-drop-warn': hasOnlyDuplicates && file
      }"
      @click="!file && clickPick()"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop="onDrop"
    >
      <input ref="inputRef" type="file" accept=".xlsx" hidden @change="onPick" />

      <!-- Empty state -->
      <template v-if="!file">
        <img
          :src="folderIconUrl"
          class="sz-drop-folder"
          alt=""
        />
        <p class="sz-drop-title">Clique ou Arraste um arquivo</p>
        <p class="sz-drop-sub">Formatos aceitos: .xlsx</p>
      </template>

      <!-- Uploading -->
      <template v-else-if="uploading">
        <div class="sz-file-icon"><span class="sz-x">X</span></div>
        <div class="sz-progress"><span :style="{ width: uploadProgress + '%' }" /></div>
        <p class="sz-progress-label">{{ uploadProgress }}% completo</p>
        <button type="button" class="sz-cancel-link" @click="clearFile">Cancelar</button>
      </template>

      <!-- Done -->
      <template v-else>
        <div class="sz-file-icon"><span class="sz-x">X</span></div>
        <p class="sz-file-name">{{ file.name }}</p>
        <div class="sz-progress sz-progress-done"><span style="width:100%" /></div>
        <p v-if="!hasErrors" class="sz-progress-label">Envio completo!</p>
        <p v-else-if="hasBlockingErrors" class="sz-progress-error">
          Erro! a sua planilha contém inconsistências. Necessário corrigir para importar
        </p>
        <p v-else class="sz-progress-warn">
          Atenção: alguns contatos já existem na base e serão ignorados
        </p>
        <button type="button" class="sz-cancel-link" @click="clickPick">
          Clique ou Arraste um arquivo
        </button>
        <p class="sz-drop-sub">Formatos aceitos: .xlsx</p>
      </template>
    </div>

    <div v-if="hasErrors" class="sz-error-cards">
      <h5>{{ hasBlockingErrors ? 'Inconsistências encontradas' : 'Aviso' }}</h5>
      <div class="sz-error-grid">
        <div v-if="errors.invalid.length" class="sz-error-card">
          <div class="sz-error-card-title"><strong>{{ errors.invalid.length }}</strong> campos inválidos</div>
          <div class="sz-error-card-lines">Linhas identificadas: {{ fmtLines(errors.invalid) }}</div>
        </div>
        <div v-if="errors.missing.length" class="sz-error-card">
          <div class="sz-error-card-title"><strong>{{ errors.missing.length }}</strong> campos faltantes</div>
          <div class="sz-error-card-lines">Linhas identificadas: {{ fmtLines(errors.missing) }}</div>
        </div>
        <div v-if="errors.duplicated.length" class="sz-error-card sz-warn-card">
          <div class="sz-error-card-title">
            <strong>{{ errors.duplicated.length }}</strong> contatos já existem
          </div>
          <div class="sz-error-card-lines">
            Linhas identificadas: {{ fmtLines(errors.duplicated) }} — serão ignoradas
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sz-step-header {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 16px;
}
.sz-step-header h4 { margin: 0; font-size: 14px; font-weight: 600; }
.sz-step-hint { color: #71717a; font-size: 12px; flex: 1; }
.sz-template-btn {
  border: 1px solid rgba(0,0,0,0.18);
  padding: 7px 14px; border-radius: 8px;
  font-size: 12px; font-weight: 500;
  text-decoration: none; color: inherit;
  background: #ffffff;
}
.sz-template-btn:hover { background: rgba(0,0,0,0.04); }


.sz-dropzone {
  border: 2px dashed rgba(0,0,0,0.16);
  border-radius: 14px;
  background: #fafafa;
  padding: 36px 22px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  text-align: center; cursor: pointer;
  min-height: 200px;
}

.sz-drop-over { border-color: #16a34a; background: rgba(22,163,74,0.06); }
.sz-drop-error {
  border-color: #dc2626;
  background: rgba(220,38,38,0.05);
}
.sz-drop-warn {
  border-color: #f59e0b;
  background: rgba(245,158,11,0.05);
}

.sz-drop-folder { width: 56px; height: auto; user-select: none; }
.sz-drop-title { font-size: 13px; font-weight: 500; margin: 4px 0 0; }
.sz-drop-sub   { font-size: 11px; color: #71717a; margin: 0; }

.sz-file-icon {
  width: 40px; height: 50px; border-radius: 4px;
  background: #16a34a; color: #ffffff;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 18px;
}
.sz-file-name { font-size: 12px; color: #71717a; margin: 4px 0; }

.sz-progress {
  width: 240px; height: 4px; border-radius: 999px;
  background: rgba(0,0,0,0.08); overflow: hidden;
}
.sz-progress span { display: block; height: 100%; background: #16a34a; transition: width 0.2s; }
.sz-progress-done span { background: #16a34a; }
.sz-progress-label { font-size: 12px; margin: 0; }
.sz-progress-error { color: #dc2626; font-size: 12px; margin: 0; }
.sz-progress-warn  { color: #b45309; font-size: 12px; margin: 0; }

.sz-cancel-link {
  border: none; background: transparent;
  color: #16a34a; font-weight: 500; cursor: pointer; font-size: 12px;
  text-decoration: underline; padding: 0;
}

.sz-error-cards {
  margin-top: 18px;
}
.sz-error-cards h5 {
  font-size: 13px; font-weight: 500; margin: 0 0 10px;
}
.sz-error-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
}
.sz-error-card {
  padding: 12px 14px;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 10px;
  background: #ffffff;
}

.sz-error-card-title { font-size: 13px; }
.sz-error-card-title strong { color: #dc2626; font-weight: 700; margin-right: 4px; }
.sz-warn-card .sz-error-card-title strong { color: #b45309; }
.sz-warn-card { border-color: rgba(245,158,11,0.4); background: rgba(245,158,11,0.05); }
.sz-error-card-lines {
  font-size: 11px; color: #71717a; margin-top: 4px; line-height: 1.4;
}

</style>
